/**
 * plan_state — "Zoom says X" is not the same fact as "X is true".
 *
 * THE BUG THIS CLOSES. Zoom can grant a Trial on any account. While it runs it looks exactly like a
 * purchased plan; when it lapses the account silently drops to Basic, /plans/usage goes quiet, and the
 * numbers already in the mirror freeze forever. The portal never read `sub_account_plans.status`, so it
 * kept rendering `account_type` (a KNOWN DECOY since migration 0013) and `sub_accounts.seats` (a leftover
 * trial figure Zoom never cleans up): ISALAMIC BANK OF THAILAND rendered "Business · 15 seats · 15
 * purchased / 1 used" for an account Zoom's own console calls Basic with no assignable licences.
 *
 * ⚠️ THE TRAP, AND WHY THERE ARE TWO EXPIRED STATES INSTEAD OF ONE.
 * `status = 'expired'` is a CLAIM FROM ZOOM, NOT A FACT — and Zoom's data is sometimes wrong. QUALICA
 * (THAILAND) has paid us for four straight years ($3,745.61, every 1 July) and flipped active→expired
 * overnight on 2026-07-13; the principal has an OPEN SUPPORT CASE with Zoom saying the expiry is wrong.
 * A naive "expired → dead" rule would badge a four-year paying customer as dead, hide them from the
 * sell-more lists, and bury them while their renewal sits in limbo. That is strictly WORSE than the bug
 * being fixed.
 *
 * So the backend (migration 0020) cross-checks Zoom's claim against OUR OWN payment history and hands the
 * frontend a state, never a verdict:
 *
 *   live             plan is running                                         → render normally
 *   lapsed_trial     expired TRIAL, never paid us a cent (50)                → a quiet FACT + a prospect
 *   paid_expired     expired, but they HAVE paid us                          → an ALERT: VERIFY, not dead
 *   expired_other    expired, not a trial, never paid (4)                    → a quiet fact
 *   cancelled        plan cancelled                                          → a quiet fact
 *   workaround_trial ⚠️ paid, but NOT on a plan we can show they bought (4)  → an OPEN INCIDENT
 *   unknown          no base-plan row, or a NULL status (892)                → say NOTHING
 *
 * ⚠️ `workaround_trial` (API 0021) IS THE STATE THAT LOOKS FINE AND IS NOT. The account paid us, Zoom
 * expired the plan they had RENEWED, and our own team applied a temporary plan so they could keep working
 * while Zoom is chased. Zoom's status therefore reads `active`, the seats are current, and every naive
 * surface renders a healthy customer — Princess Galyani Vadhana Institute of Music ($10,364.55, renewed,
 * killed by a Zoom fault) was being offered to a salesperson as a TRIAL CONVERSION PROSPECT. It is not a
 * healthy account and it is not a prospect: it is an OPEN INCIDENT in a trial coat, and the fix we applied
 * is precisely what hides it.
 *
 * `unknown` IS NOT `expired`. 892 ordinary Basic/free accounts have no base-plan row, and an over-eager
 * expired verdict on them would hide live customers — the exact harm this change exists to prevent. Every
 * helper below therefore fails SAFE: an unrecognised / absent state is treated as `unknown`, which badges
 * nothing, freezes nothing and hides nothing.
 */

import type { PlanState } from "@/api/types";

/**
 * The states in which the base plan is PROVABLY not live — so the seat / licence / utilization figures
 * still sitting in the mirror are FROZEN leftovers that Zoom will never refresh again.
 *
 * Mirrors `_PLAN_STATE_NOT_LIVE` in the API's customers/service.py. `live` and `unknown` are deliberately
 * absent: we only suppress a figure where we can PROVE it is stale.
 *
 * ⚠️ `workaround_trial` IS DELIBERATELY ABSENT TOO, for the OPPOSITE reason, and this is the subtle half
 * of 0021. Its plan is genuinely ACTIVE (the temporary one laid over the top), so Zoom IS still answering
 * /plans/usage and those seat figures ARE current. Freezing them would be a lie in the other direction —
 * and worse, a freeze notice would make it look like a dead account when the truth is the exact opposite:
 * the customer is working fine and the VENDOR is broken. What that account needs is not "these numbers are
 * stale", it is "this is an open incident". ⚠️ NOTE (2026-07-15): the portal NO LONGER surfaces that
 * incident anywhere — the plan_state badge collapsed to a four-value account status (lib/accountStatus.ts)
 * and workaround_trial maps to a plain "Active" pill by product decision; the Plan-incidents worklist and
 * the Customer-360 banner are both deleted. What still matters HERE is only the freeze half: keeping
 * workaround_trial OUT of NOT_LIVE so its genuinely-current numbers are not falsely frozen. The incident
 * detail lives in the backend v_plan_incident view now, not this file.
 */
const NOT_LIVE = new Set<PlanState>(["lapsed_trial", "paid_expired", "expired_other", "cancelled"]);

/**
 * Every state the backend can emit — and the ONE structure in this file the compiler polices.
 *
 * `Record<PlanState, true>` is EXHAUSTIVE: adding a member to the PlanState union and forgetting this map
 * is a compile error, so `planStateOf` can never again meet a state it silently downgrades. It is written
 * as a map rather than an array for exactly that reason (an array literal missing a member is legal
 * TypeScript — the same trap that let a hand-written worklist union drift out of step).
 */
const ALL_STATES: Record<PlanState, true> = {
  live: true,
  lapsed_trial: true,
  paid_expired: true,
  expired_other: true,
  cancelled: true,
  workaround_trial: true,
  unknown: true,
};

const KNOWN = new Set<string>(Object.keys(ALL_STATES));

/**
 * True when the plan is provably dead and its frozen numbers must not be rendered as current.
 *
 * Takes `unknown` (not `PlanState`) on purpose: the value arrives from a JSON payload and a role/route
 * that predates 0020 simply omits it. An absent, null or unrecognised value is NOT evidence of death.
 */
export function isPlanNotLive(state: unknown): boolean {
  return typeof state === "string" && NOT_LIVE.has(state as PlanState);
}

/**
 * Normalise whatever the payload carried into a PlanState. Anything we do not recognise — absent, null,
 * a state a future backend adds — becomes `unknown`, i.e. "we are not going to make a claim".
 *
 * The membership test is `ALL_STATES` — which the compiler forces to stay exhaustive — NOT a hand-written
 * list of the states this function happened to know about when it was written. That distinction is not
 * theoretical: the 0020 version whitelisted `live | unknown | …NOT_LIVE`, so the moment 0021 began
 * emitting `workaround_trial` it would have silently downgraded it to `unknown` — no badge, no notice, a
 * $10,365 open incident rendered as an ordinary account, with every test still green. A NEW state falling
 * through to `unknown` is not a safe default here; it is the exact failure this change exists to kill.
 */
export function planStateOf(state: unknown): PlanState {
  if (typeof state !== "string") return "unknown";
  return KNOWN.has(state) ? (state as PlanState) : "unknown";
}

/**
 * The ONE state that may be filtered out of the three sell-more lists (and only in SQL, in the leaf
 * views — never here, and never on search / Customer-360 / a count).
 *
 * Exported so the intent is greppable and testable from the frontend, and so nobody ever reaches for
 * `isPlanNotLive` when they mean this. Selling MORE to someone who bought NOTHING is noise; hiding
 * someone who HAS paid us is the failure mode.
 */
export function isHideableFromSellMore(state: unknown): boolean {
  return planStateOf(state) === "lapsed_trial";
}

/**
 * ⚠️ The states that must NEVER be hidden from anything — not a worklist, not search, not a count.
 * They are PAYING customers whose plan Zoom has called into question, in the two shapes that question
 * takes: still expired (`paid_expired`), or patched by us and therefore invisible (`workaround_trial`).
 *
 * The second one is why this is a set and not an equality check. A `workaround_trial` account reads
 * `active` everywhere; if any surface decides what to hide by asking "is it expired?", the two accounts
 * our own team RESCUED are the two it will hide.
 */
export function isPaidIncident(state: unknown): boolean {
  const s = planStateOf(state);
  return s === "paid_expired" || s === "workaround_trial";
}

/**
 * ⚠️ A PAYING account, expired by Zoom, and then patched onto a temporary plan (API 0021) — by our own
 * team, or by Zoom itself. The data cannot say which, so nothing here claims to know.
 *
 * Its plan is active and its numbers are current, so it is NOT not-live and must NOT be frozen. It is
 * also NOT a trial, NOT a prospect, and NOT a healthy customer — it is an OPEN INCIDENT that happens to
 * be wearing a trial coat, and the Zoom fault behind it is still open.
 *
 * ⚠️ No UI consumes this predicate any more (2026-07-15): the plan_state badge it used to drive was
 * replaced by the four-value account status, under which workaround_trial simply reads "Active". Kept as
 * the documented, greppable definition of the state (the backend v_plan_incident view is the live surface).
 */
export function isWorkaroundTrial(state: unknown): boolean {
  return planStateOf(state) === "workaround_trial";
}
